import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { TouchableOpacity } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { AppView, Button, hitSlop, NavigationHeader, rem, SCROLL_INDICATOR_INSETS, } from 'design-system';
import { IconMoneyBills, IconWallet } from 'design-system/icons';
import IconLoan from 'design-system/icons/IconLoan';
import IconPlus from 'design-system/icons/IconPlus';
import Indicator from 'design-system/Indicator';
import ListEmptyView from 'design-system/ListEmptyView';
import Modal from 'utils/packages/Modal';
import { PickerModal } from 'design-system/picker/PickerModal';
import Text from 'design-system/Text';
import { useActionSheet } from 'hooks/useActionSheet';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import Animated, { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import createStyleSheets from 'utils/createStyleSheets';
import { applyToSwitching, getEnergySwitchingResults, getSwitchingQuotes, } from '../../../../../actions';
import BroadBandQuotesHeader from '../../broadband/BroadBandQuotesHeader';
import EnergyFilters from '../components/EnergyFilters';
import QuoteCard from '../components/QuoteCard';
import Amplitude from '../../../../../utils/amplitude';
import { averageMonthlyCost, averageYearlyCost, averageYearlySavings, } from '../EnergySorting';
import { isWeb } from '../../../../../constants';
const options = [
    { label: 'Yearly Costs', value: 'Yearly Costs', icon: <IconLoan /> },
    { label: 'Monthly Costs', value: 'Monthly Costs', icon: <IconMoneyBills /> },
    { label: 'Yearly Savings', value: 'Yearly Savings', icon: <IconWallet /> },
];
const keyExtractor = (item) => item.data.id.toString();
const EnergyQuotesScreen = ({ navigation, route, }) => {
    const { height } = useAppFrameDimensions(isWeb);
    const colors = useColors();
    const scrollY = useSharedValue(0);
    const styles = useStyles(styleSet);
    const actionSheet = useActionSheet();
    const onPressAddNewQuote = useCallback(() => {
        actionSheet.showActionSheetWithOptions({
            title: 'Would you like to get a new quote?',
            options: ['Yes, let’s do it', 'Cancel'],
            cancelButtonIndex: 1,
        }, async (index) => {
            if (index !== 0)
                return;
            const body = {
                partner: 'MONEYSUPERMARKET',
                type: 'ENERGY',
            };
            const response = await dispatch(getSwitchingQuotes(body));
            if (response?.payload) {
                const secondBody = {
                    action: 'ADVANCED_ENQUIRY',
                };
                const { enquiryId } = response.payload;
                const secondResponse = await dispatch(getEnergySwitchingResults(enquiryId, secondBody));
                if (secondResponse?.payload) {
                    navigation.replace('AdvancedEnquiry', {
                        data: secondResponse.payload,
                    });
                }
            }
        });
    }, [actionSheet]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const isFetchingResults = useAppSelector((store) => store.switching.isFetchingResults);
    const enquiries = useAppSelector((store) => store.switching.enquiries);
    const personalProjection = useAppSelector((store) => store.switching.personalProjection);
    const [state, setState] = useState({
        defaultFilters: {
            tariff_type: 0,
            show_me: 0,
            isGreeen: undefined,
        },
        isAllDeals: 0,
        selectedSortingIndex: 2,
        results: enquiries,
        filterState: {
            tariffType: 0,
            tariffFeatures: 0,
            showMe: 0,
            isGreenProvider: 0,
        },
    });
    useEffect(() => {
        setState((state) => ({ ...state, results: enquiries }));
    }, [enquiries]);
    useEffect(() => {
        Amplitude.logEvent('MoneySuperMarket.EnergyQuotes');
    }, []);
    const onOpenQuote = useCallback((data) => {
        const { enquiryId } = route.params;
        navigation.navigateDeprecated('Quote', { data, enquiryId });
        Amplitude.logEvent('MoneySuperMarket.EnergyQuotesOpenQuote');
    }, [route.params]);
    const dispatch = useAppDispatch();
    const onPressRow = useCallback((data) => {
        const { params } = route;
        const { enquiryId } = params;
        Promise.resolve(dispatch(applyToSwitching(enquiryId, data.id))).then((response) => {
            if (response &&
                response.payload &&
                response.payload.webview &&
                response.payload.webview.url) {
                navigation.navigateDeprecated('EnergyApplication', {
                    data: response.payload,
                    name: data.data.supplier.name,
                });
            }
            return response;
        });
        Amplitude.logEvent('MoneySuperMarket.EnergyQuotesClickSwitchNow');
    }, [route]);
    const onPersonalProjection = () => {
        navigation.navigateDeprecated('PersonalProjection', {
            personalProjection,
        });
    };
    const marginBottom = useMarginBottom('marginBottom', undefined, styles.closeButton);
    const onPressFilters = () => {
        Modal.show(<EnergyFilters state={state.filterState} onFiltering={onFiltering}/>, {
            title: 'Filter by',
            scrollable: {
                maxHeight: height * 0.7,
                outsideContent: (<Button brand title="Close" onPress={() => {
                        Modal.hide();
                    }} style={marginBottom}/>),
            },
        });
        Amplitude.logEvent('MoneySuperMarket.EnergyQuotesOpenFilters');
    };
    const onPressSelector = () => {
        PickerModal.show({
            pickerOptions: options,
            onSelected: (_value, index) => {
                onPositionChange(index);
            },
            selectedValue: options[state.selectedSortingIndex],
            selectedIconFill: colors.gradients.lightPink.colors[0],
        }, {
            title: 'Sort by',
        });
        Amplitude.logEvent('MoneySuperMarket.EnergyQuotesOpenSorting');
    };
    const onFiltering = (filter, value, newFilterState) => {
        setState((state) => ({
            ...state,
            defaultFilters: {
                ...state.defaultFilters,
                [filter]: value,
            },
            filterState: newFilterState,
        }));
    };
    const onPositionChange = (data) => {
        setState((state) => ({ ...state, selectedSortingIndex: data }));
    };
    const renderItem = useCallback(({ item }) => (<QuoteCard item={item} onPress={onPressRow} onInfoPressed={onOpenQuote}/>), [onOpenQuote, onPressRow]);
    const resultsArray = useMemo(() => {
        // eslint-disable-next-line prefer-destructuring
        const results = state.results;
        // eslint-disable-next-line prefer-destructuring
        const defaultFilters = state.defaultFilters;
        let resultsArray = [...results];
        resultsArray = resultsArray.filter(({ data }) => data && data.tariff && data.tariff.onlineApplicationPermitted);
        if (defaultFilters.show_me === 1) {
            resultsArray = results;
        }
        if (defaultFilters.tariff_type === 1) {
            resultsArray = resultsArray.filter(({ data }) => data && data.tariff && data.tariff.rateType === 'Fixed');
        }
        if (defaultFilters.tariff_type === 2) {
            resultsArray = resultsArray.filter(({ data }) => data && data.tariff && data.tariff.rateType === 'Variable');
        }
        if (defaultFilters.isGreeen) {
            resultsArray = resultsArray.filter(({ data }) => data && data.tariff && !data.tariff.isRenewable);
        }
        switch (options[state.selectedSortingIndex].value) {
            case 'Monthly Costs':
                return resultsArray.slice().sort(averageMonthlyCost);
            case 'Yearly Savings':
                return resultsArray.slice().sort(averageYearlySavings);
            case 'Yearly Costs':
            default:
                return resultsArray.slice().sort(averageYearlyCost);
        }
    }, [state.results, state.defaultFilters, state.selectedSortingIndex]);
    const renderEmpty = useMemo(() => isFetchingResults ? (<Indicator dark style={styles.someMarginTop}/>) : (<ListEmptyView onCard title="No results" subTitle={`Update your search to see\nsomething here`}/>), [isFetchingResults, styles.someMarginTop]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const headerRight = useMemo(() => (<TouchableOpacity style={styles.newQuoteButton} onPress={onPressAddNewQuote} hitSlop={hitSlop}>
        <IconPlus color={colors.text.primary}/>
      </TouchableOpacity>), [colors.text.primary, onPressAddNewQuote, styles.newQuoteButton]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', undefined, styles.contentContainerStyle, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} rightContent={headerRight} title="Your energy deals" withFrame>
        <BroadBandQuotesHeader scrollY={scrollY} onPress={onPressSelector} onPressFilters={onPressFilters} label={options[state.selectedSortingIndex].label}/>
        <TouchableBounce onPress={onPersonalProjection} style={styles.personalProjection}>
          <Text TextThin-16 OnBrand>
            See my personal projection
          </Text>
        </TouchableBounce>
      </NavigationHeader>
      <Animated.FlatList data={resultsArray} renderItem={renderItem} onScroll={scrollHandler} onEndReachedThreshold={0.8} keyExtractor={keyExtractor} ListEmptyComponent={renderEmpty} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    contentContainerStyle: {
        paddingTop: rem(16),
    },
    someMarginTop: {
        marginTop: rem(100),
    },
    personalProjection: {
        height: rem(48),
        alignItems: 'center',
        marginVertical: rem(12),
        borderRadius: rem(12),
        marginHorizontal: rem(16),
        justifyContent: 'center',
        backgroundColor: colors.buttons.brandGroup,
    },
    newQuoteButton: {
        alignItems: 'flex-end',
    },
    closeButton: {
        marginHorizontal: rem(16),
    },
}));
export default EnergyQuotesScreen;
